/**
 * Coach Programs — list of coach's training programs.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, RefreshControl, Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { loadPrograms, deleteProgram, type Program } from '@/lib/programBuilder';
import { ArrowLeft, Plus, ClipboardList, Trash2 } from 'lucide-react-native';

export default function CoachPrograms() {
  const { theme } = useTheme();
  const c = theme.colors;
  const router = useRouter();
  const { profile } = useAuth();

  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!profile?.id) return;
    const list = await loadPrograms(profile.id);
    setPrograms(list);
  }, [profile?.id]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const onRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handleDelete = (prog: Program) => {
    Alert.alert(
      'Delete Program',
      `Delete "${prog.name}"? This cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            await deleteProgram(prog.id);
            await load();
          },
        },
      ],
    );
  };

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.back()}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          style={styles.backBtn}
        >
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: c.text }]}>Programs</Text>
        <View style={{ width: 30 }} />
      </View>

      {loading ? (
        <ActivityIndicator color={c.accent} style={{ marginTop: 60 }} />
      ) : (
        <ScrollView
          contentContainerStyle={styles.scroll}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />}
        >
          {programs.length === 0 ? (
            <View style={styles.emptyContainer}>
              <ClipboardList size={48} color={c.textTertiary} />
              <Text style={[styles.emptyTitle, { color: c.textSecondary }]}>No programs yet</Text>
              <Text style={[styles.emptyHint, { color: c.textTertiary }]}>
                Tap the + button to create your first program.
              </Text>
            </View>
          ) : (
            programs.map((prog) => (
              <View
                key={prog.id}
                style={[styles.programCard, { backgroundColor: c.surface, borderColor: c.border }]}
              >
                <TouchableOpacity
                  style={{ flex: 1 }}
                  onPress={() =>
                    router.push({
                      pathname: '/coach-program-builder',
                      params: { programId: prog.id },
                    } as any)
                  }
                >
                  <Text style={[styles.programName, { color: c.text }]}>{prog.name}</Text>
                  <Text style={[styles.programMeta, { color: c.textSecondary }]}>
                    {prog.days.length} day{prog.days.length !== 1 ? 's' : ''}
                    {prog.assignedTo && prog.assignedTo.length > 0
                      ? ` · ${prog.assignedTo.length} assigned`
                      : ' · Not assigned'}
                  </Text>
                </TouchableOpacity>
                <View style={styles.cardActions}>
                  <TouchableOpacity
                    style={[styles.editBtn, { backgroundColor: c.accent + '18' }]}
                    onPress={() =>
                      router.push({
                        pathname: '/coach-program-builder',
                        params: { programId: prog.id },
                      } as any)
                    }
                  >
                    <Text style={[styles.editBtnText, { color: c.accent }]}>Edit</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    onPress={() => handleDelete(prog)}
                  >
                    <Trash2 size={16} color={c.textTertiary} />
                  </TouchableOpacity>
                </View>
              </View>
            ))
          )}
          <View style={{ height: 80 }} />
        </ScrollView>
      )}

      {/* FAB — New Program */}
      <TouchableOpacity
        style={[styles.fab, { backgroundColor: c.accent }]}
        onPress={() =>
          router.push({ pathname: '/coach-program-builder' } as any)
        }
      >
        <Plus size={26} color={c.accentForeground} />
      </TouchableOpacity>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    justifyContent: 'space-between',
  },
  backBtn: { padding: 4 },
  headerTitle: { fontSize: 20, fontFamily: TYPO.heading.bold },
  scroll: { padding: 16 },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 60,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 16,
    fontFamily: TYPO.body.semibold,
  },
  emptyHint: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
    textAlign: 'center',
    paddingHorizontal: 24,
  },
  programCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 2,
    padding: 14,
    marginBottom: 10,
    gap: 10,
  },
  programName: {
    fontSize: 15,
    fontFamily: TYPO.body.bold,
    marginBottom: 2,
  },
  programMeta: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
  },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  editBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 2,
  },
  editBtnText: {
    fontSize: 13,
    fontFamily: TYPO.body.bold,
  },
  fab: {
    position: 'absolute',
    bottom: 32,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
  },
});
